// Roll20-style number field: shows the value; click to edit; Enter or blur
// applies, Escape cancels.
//
// ONE RULE FOR EVERY NUMBER. A bare "35" sets the value; "-3" deducts three
// from whatever is there; "+5" adds five. Hit points, armour class, a saving
// throw and a countdown all behave the same way, so there is nothing to
// remember per field.
//
// This briefly had a second mode where a leading sign belonged to the number,
// so "-3" in a saving throw meant the modifier WAS minus three. It was removed
// on the grounds that one rule is easier to hold than two. The consequence is
// worth knowing: a negative value is reached by deducting rather than typed
// directly - from a save of +2, "-3" gives -1. From 0, "-1" gives -1.
//
// Names and other text are not edited here at all. Text does not do arithmetic.
import { useEffect, useRef, useState } from "react";

export function applyEntry(current: number, entry: string): number | null {
  const text = entry.trim().replace(/\s+/g, "");
  if (!text) return null;
  const m = text.match(/^([+-])?(\d+)$/);
  if (!m) return null;
  const n = Number(m[2]);
  if (m[1] === "-") return current - n;
  if (m[1] === "+") return current + n;
  return n;
}

export function NumberField({ value, onApply, label, className, title, min = 0, max }: { value: number; onApply: (next: number, entry: string) => void; label: string; className?: string; title?: string; min?: number; max?: number }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { input.current?.focus(); input.current?.select(); } }, [editing]);
  const commit = () => {
    const next = applyEntry(Number(value || 0), text);
    setEditing(false);
    if (next === null) return;
    const clamped = Math.max(min, max !== undefined ? Math.min(max, next) : next);
    if (clamped !== Number(value || 0)) onApply(clamped, text.trim());
  };
  if (!editing) return <button type="button" className={`numfield ${className || ""}`} onClick={(e) => { e.stopPropagation(); setText(String(value)); setEditing(true); }} aria-label={`${label}: ${value} (click to edit; -10 subtracts, +5 adds, 35 sets)`} title={title || "Click: -10 subtracts, +5 adds, 35 sets"}>{value}</button>;
  return <input ref={input} className={`numfield editing ${className || ""}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }} onBlur={commit} onClick={(e) => e.stopPropagation()} aria-label={label} inputMode="numeric" />;
}
